import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { Course } from '../store/useStore'

/**
 * #737: List courses for a store. Hits REST API once per (storeId, includeInactive).
 *
 * Owners pass `includeInactive=true` from the management dashboard so inactive
 * courses still show up for re-activation. Customer-facing views default to
 * false and only see active courses.
 */
export function useCoursesByStore(
  storeId: string | null | undefined,
  options: { includeInactive?: boolean } = {},
) {
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(!!storeId)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const includeInactive = !!options.includeInactive

  // Reset state synchronously when any input (target store / includeInactive /
  // refresh trigger) changes — derive-state-from-props avoids setState-in-effect
  // anti-pattern flagged by react-hooks/set-state-in-effect.
  const inputKey = `${storeId ?? ''}|${includeInactive ? '1' : '0'}|${refreshKey}`
  const [trackedKey, setTrackedKey] = useState(inputKey)
  if (inputKey !== trackedKey) {
    setTrackedKey(inputKey)
    setCourses([])
    setLoading(!!storeId)
    setError(null)
  }

  useEffect(() => {
    if (!storeId) return
    let cancelled = false
    api.listCoursesByStore(storeId, includeInactive)
      .then((res) => {
        if (cancelled) return
        setCourses(res.courses ?? [])
        setLoading(false)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setError(err)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, includeInactive, refreshKey])

  const refresh = () => setRefreshKey((k) => k + 1)
  return { courses, loading, error, refresh }
}

/**
 * #737: Single course detail loader.
 */
export function useCourse(storeId: string | null | undefined, courseId: string | null | undefined) {
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(!!(storeId && courseId))
  const [error, setError] = useState<Error | null>(null)

  // Reset state when the (storeId, courseId) target changes — derive from props
  // to avoid the setState-in-effect anti-pattern.
  const targetKey = `${storeId ?? ''}|${courseId ?? ''}`
  const [trackedKey, setTrackedKey] = useState(targetKey)
  if (targetKey !== trackedKey) {
    setTrackedKey(targetKey)
    setCourse(null)
    setLoading(!!(storeId && courseId))
    setError(null)
  }

  useEffect(() => {
    if (!storeId || !courseId) return
    let cancelled = false
    api.getCourse(storeId, courseId)
      .then((c) => {
        if (cancelled) return
        setCourse(c)
        setLoading(false)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setError(err)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, courseId])

  return { course, loading, error }
}
